import { useEffect, useState } from 'react';

import { de } from '../i18n/de';
import { phraseRepo } from '../data/repositories';
import { speechService } from '../core/speech/SpeechService';
import { ActivateButton } from './ActivateButton';

/**
 * Conversational repair (3.8).
 *
 * 3.8's own note is the reason this exists: "these are small and easy to dismiss, but they are what let the
 * person manage a conversation rather than just emit sentences — the difference between answering and
 * participating."
 *
 * Shown in the reserved band when nothing is being typed — see `bandTenant.ts` for why there and not in a row of
 * its own (C65). One tenant of four; `PredictionRow` owns the band's frame and its height, and the layer switch
 * sits outside this row entirely (ADR-039). The low-effort toggle used to be this row's fifth child and is now
 * a caregiver's control in Betreuer-Modus (`SUR-13`).
 *
 * ── The phrases already shipped ──
 *
 * Three of 3.8's four are seeded in `cat-gespraech` and already pre-rendered by the existing pass, so "instant"
 * needs no new machinery. What 3.8 actually asked for was to bring them **up from two taps deep in Sätze**.
 *
 * Each entry reads the live phrase by id and falls back to the seeded text. That is deliberate in both
 * directions: a caregiver who rewords the tile in Sätze sees the wording change here too, and a caregiver who
 * *deletes* it does not break the row. A repair row that vanished because somebody tidied a category would be a
 * poor thing to discover mid-conversation.
 */

interface RepairEntry {
  /** Seeded id, so a caregiver's edit to that tile is honoured here. */
  phraseId: string;
  /** Used if the tile has been deleted. */
  fallbackText: string;
  label: string;
}

/**
 * 3.8 asks for `Nicht das · Frag mich anders · Warte, ich schreibe · Ja, genau`.
 *
 * The seeded phrases say the same four things in the wording a caregiver already reviewed, so those are used
 * rather than four new strings — "Bitte wiederhol das." for "frag mich anders", which is the same request made
 * more plainly.
 */
const REPAIR: readonly RepairEntry[] = [
  { phraseId: 'ph-gespraech-6', fallbackText: de.repairNotThatText, label: de.repairNotThatLabel },
  { phraseId: 'ph-gespraech-4', fallbackText: de.repairAgainText, label: de.repairAgainLabel },
  { phraseId: 'ph-gespraech-2', fallbackText: de.repairWaitText, label: de.repairWaitLabel },
  { phraseId: 'ph-gespraech-5', fallbackText: de.repairYesText, label: de.repairYesLabel },
];

export function RepairRow(): React.JSX.Element {
  const [texts, setTexts] = useState<Record<string, string>>({});

  useEffect(() => {
    // Read once. These phrases change when a caregiver edits them, which is not something that happens while
    // the person is holding a conversation.
    void phraseRepo.all().then((phrases) => {
      const found: Record<string, string> = {};
      for (const entry of REPAIR) {
        const phrase = phrases.find((candidate) => candidate.id === entry.phraseId);
        if (phrase) found[entry.phraseId] = phrase.text;
      }
      setTexts(found);
    });
  }, []);

  return (
    <div style={{ display: 'flex', gap: 'var(--key-gap)', height: '100%', flex: '1 1 0', minWidth: 0 }}>
      {REPAIR.map((entry) => (
        <ActivateButton
          key={entry.phraseId}
          id={`repair:${entry.phraseId}`}
          onActivate={() => {
            void speechService.speak({
              text: texts[entry.phraseId] ?? entry.fallbackText,
              source: 'phrase',
              // Cached like any phrase, so the second use is instant and the offline path has it.
              isPhrase: true,
            });
          }}
          style={{
            flex: '1 1 0',
            height: '100%',
            fontSize: 'var(--font-body)',
            // Drawn exactly like a candidate. It is the same band, the same height and the same kind of
            // act — tap it and something is said — so it has no business looking like a different
            // species of thing depending on which tenant happens to be in the band.
            background: 'var(--key-bg)',
            color: 'var(--key-fg)',
            border: '3px solid var(--key-border)',
            borderRadius: 'var(--radius)',
          }}
        >
          {entry.label}
        </ActivateButton>
      ))}
    </div>
  );
}
